import { useNavigate } from "react-router-dom";
import { useState } from "react";
import Logo from "../assets/images/logo.png";
import SelectList from "../components/SelectList";
import { API_BASE } from "../utils/Instance";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const roles = [
  { label: "Content Contributor", value: "contributor" },
  { label: "User", value: "user" },
];

export default function Login() {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState(roles[0]); // UI only, backend decides real role
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState("");
  const navigate = useNavigate();
  const { login, logout, clearSession } = useAuth();

  const handleLogin = async (e) => {
    e.preventDefault();
    clearSession();

    try {
      const requestUrl = `${API_BASE}accounts/login/`;

      console.log("🔹 Final Request URL:", requestUrl);
      console.log("🔹 Login Payload:", { email, password });

      const resp = await fetch(requestUrl, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      console.log("🔹 Response Status:", resp.status);

      const text = await resp.text();
      console.log("🔹 Raw Response Text:", text);

      if (!resp.ok) throw new Error("Login failed");

      // Backend returns access token in JSON and stores refresh token in an HttpOnly cookie.
      const { access, profile } = JSON.parse(text);
      if (!access) throw new Error("Login response did not include an access token");
      console.log("🔹 Parsed Profile:", profile);

      // Enforce selected role matches backend role
      const selectedRole = role?.value;
      if (selectedRole && profile?.role && profile.role !== selectedRole) {
        await logout();
        toast.error(`This account is registered as "${profile.role}". Please select the correct role to log in.`);
        return; // stop login if role mismatch
      }

      // Save profile + tokens in context, normalize screenName for immediate UI
      login(access, null, {
        ...profile,
        screenName: profile?.screen_name || profile?.screenName,
      });

      // Navigate by role
      if (profile.role === "user") {
        navigate("/dashboard");
      } else if (profile.role === "contributor") {
        navigate("/creator-dashboard");
      } else {
        navigate("/dashboard"); // fallback
      }
    } catch (err) {
      console.error("❌ Login error:", err);
      alert("Login failed. Check your email/password.");
    }
  };

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-black">
      {/* LEFT SIDE */}
      <div className="hidden md:flex flex-1 flex-col justify-center items-center p-8 md:p-16">
        <div className="max-w-md">
          <div className="flex flex-col items-center md:items-start">
            <div className="mb-6">
              <img src={Logo} alt="Select Exposure" className="w-[360px]" />
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT SIDE */}
      <div className="flex-1 flex justify-center items-center p-4 sm:p-8">
        <div className="w-full max-w-md bg-[#1c1c1e] p-6 sm:p-8 rounded-lg shadow-lg">
          <div className="flex justify-center mb-6">
            <img src={Logo} alt="Logo" className="w-[300px]" />
          </div>

          <h3 className="text-2xl font-semibold text-[#CEB46A] text-center mb-6">
            Login
          </h3>

          <form onSubmit={handleLogin} className="space-y-4">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-white mb-1">
                Email<span className="text-white">*</span>
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="w-full py-2 px-4 bg-[#1c1c1e] border border-gray-600 rounded focus:outline-none focus:border-[#ceb46a] text-white"
                required
              />
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-white mb-1">
                Password<span className="text-white">*</span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full py-2 px-4 bg-[#1c1c1e] border border-gray-600 rounded focus:outline-none focus:border-[#ceb46a] text-white pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400"
                >
                  {showPassword ? "🙈" : "👁️"}
                </button>
              </div>
              <div className="flex justify-end mt-1">
                <a
                  href="/forgot-password"
                  className="text-[#ceb46a] text-sm mb-1"
                >
                  Forgot Password?
                </a>
              </div>
            </div>

            {/* Role dropdown (just for UI, backend decides real role) */}
            <SelectList
              className="border"
              buttonClassName="bg-transparent"
              label=""
              options={roles}
              value={role}
              onChange={setRole}
            />

            <div className="pt-4">
              <button
                type="submit"
                className="w-full py-3 bg-[#ceb46a] text-black font-medium rounded hover:bg-[#b9a05a] transition duration-300 focus:outline-none"
              >
                Login
              </button>
            </div>
          </form>

          <div className="mt-4 text-center">
            <p className="text-gray-400">
              Don&apos;t have an account?{" "}
              <a href="/signup" className="text-[#ceb46a]">
                Sign Up
              </a>
            </p>
          </div>

          <div className="mt-2 text-center text-base leading-5 text-gray-400">
            <a href="/conditions" className="block hover:text-[#ceb46a] transition-colors">
              Terms &amp; Conditions
            </a>
            <a href="/faqs" className="block hover:text-[#ceb46a] transition-colors">
              FAQs
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
